import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, ChefHat, ShoppingBag, Lightbulb, Share2 } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";

export default function RecipeDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [recipe, setRecipe] = useState(null);
  const [products, setProducts] = useState({});
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    api
      .get(`/recipes/${slug}`)
      .then(async (res) => {
        setRecipe(res.data);
        const all = await api.get("/products");
        const ids = res.data.ingredients.map((i) => i.product_id).filter(Boolean);
        const map = {};
        all.data.forEach((p) => {
          if (ids.includes(p.id)) map[p.id] = p;
        });
        setProducts(map);
        // Suggested: featured products NOT already in this recipe
        const sug = all.data
          .filter((p) => p.featured && !ids.includes(p.id))
          .slice(0, 4);
        setSuggested(sug);
      })
      .catch(() => setRecipe(null))
      .finally(() => setLoading(false));
  }, [slug]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const shopRecipe = () => {
    if (!recipe) return;
    let added = 0;
    recipe.ingredients.forEach((ing) => {
      if (ing.product_id && products[ing.product_id]) {
        added += addItem(products[ing.product_id], 1) > 0 ? 1 : 0;
      }
    });
    if (added === 0) {
      toast.info(shopableCount ? "These ingredients are out of stock right now." : "This recipe has no shop-able ingredients linked yet.");
    } else {
      toast.success(`Added ${added} ingredient${added === 1 ? "" : "s"} to your basket!`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-5 py-20">
        <div className="aspect-[16/9] bg-[var(--muted)] rounded-3xl animate-pulse" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h2 className="font-serif text-4xl text-[var(--ink-forest)]">Recipe not found</h2>
        <Link
          to="/recipes"
          className="mt-6 inline-block bg-[var(--terracotta)] text-white rounded-full px-6 py-3 text-sm"
        >
          Back to recipes
        </Link>
      </div>
    );
  }

  const shopableCount = recipe.ingredients.filter((i) => i.product_id && products[i.product_id]).length;

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
    pageUrl
  )}&media=${encodeURIComponent(recipe.image_url)}&description=${encodeURIComponent(
    `${recipe.title} — ${recipe.short_description} | DC Gift World Too`
  )}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: recipe.title,
          text: recipe.short_description,
          url: pageUrl,
        });
      } catch (e) {
        // user cancelled
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(pageUrl);
        toast.success("Recipe link copied!");
      } catch (e) {
        toast.error("Could not copy link");
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="recipe-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--muted-fg)] hover:text-[var(--terracotta)] mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Hero */}
      <div className="rounded-3xl overflow-hidden aspect-[16/10] mb-3">
        <img src={resolveImg(recipe.image_url)} alt={recipe.title} className="w-full h-full object-cover" />
      </div>
      <p className="text-xs italic text-[var(--muted-fg)] text-right mb-7" data-testid="recipe-image-disclaimer">
        Image for inspiration — your handmade results will be uniquely yours.
      </p>

      <p className="eyebrow mb-3">{categoryLabel(recipe.category)}</p>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
          {recipe.title}
        </h1>
        <div className="flex gap-2">
          <a
            href={pinterestUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#E60023] text-white rounded-full px-4 py-2 text-xs font-semibold hover:opacity-90 transition-opacity"
            data-testid="pinterest-share-btn"
            title="Pin this recipe to Pinterest"
          >
            <PinterestIcon className="w-3.5 h-3.5" />
            Pin it
          </a>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[var(--muted)] transition-colors"
            data-testid="recipe-share-btn"
            title="Share this recipe"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        </div>
      </div>
      <p className="mt-4 text-lg text-[var(--muted-fg)] leading-relaxed">{recipe.short_description}</p>

      <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-[var(--muted-fg)] border-t border-b border-[var(--border)] py-4">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-[var(--terracotta)]" /> {recipe.time_minutes} minutes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ChefHat className="w-4 h-4 text-[var(--terracotta)]" /> {recipe.difficulty}
        </span>
        {recipe.yield_text && (
          <span className="inline-flex items-center gap-1.5">
            ✦ Makes {recipe.yield_text}
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-10 mt-10">
        {/* Ingredients */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 lg:sticky lg:top-28">
            <h2 className="font-serif text-2xl text-[var(--ink-forest)] mb-5">Ingredients</h2>
            <ul className="space-y-3" data-testid="ingredients-list">
              {recipe.ingredients.map((ing, i) => {
                const product = ing.product_id ? products[ing.product_id] : null;
                return (
                  <li key={i} className="text-sm pb-3 border-b border-[var(--border)] last:border-0 last:pb-0">
                    <div className="flex justify-between gap-2">
                      <div className="flex-1">
                        {product ? (
                          <Link
                            to={`/product/${product.id}`}
                            className="font-medium text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-2 decoration-dotted"
                            data-testid={`ingredient-link-${i}`}
                          >
                            {ing.name}
                          </Link>
                        ) : (
                          <span className="font-medium text-[var(--ink-forest)]">{ing.name}</span>
                        )}
                      </div>
                      <span className="text-[var(--muted-fg)] whitespace-nowrap">{ing.amount}</span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {shopableCount > 0 && (
              <button
                onClick={shopRecipe}
                className="mt-5 w-full bg-[var(--terracotta)] text-white rounded-full py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="shop-recipe-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                Shop this recipe ({shopableCount})
              </button>
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="lg:col-span-3">
          <h2 className="font-serif text-2xl text-[var(--ink-forest)] mb-5">Steps</h2>
          <ol className="space-y-5" data-testid="steps-list">
            {recipe.steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--terracotta)] text-white flex items-center justify-center font-serif text-lg">
                  {i + 1}
                </div>
                <p className="text-[var(--ink-forest)] leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>

          {recipe.tips && (
            <div className="mt-10 bg-[var(--muted)]/50 rounded-2xl p-5 border-l-4 border-[var(--gold)]" data-testid="recipe-tips">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-[var(--gold)] shrink-0 mt-0.5" />
                <div>
                  <p className="eyebrow text-[var(--gold)] mb-1.5">Notes from the kitchen</p>
                  <p className="text-sm text-[var(--ink-forest)] leading-relaxed whitespace-pre-line">{recipe.tips}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 text-xs text-[var(--muted-fg)] border-t border-[var(--border)] pt-5">
            Always patch test essential oils on a small area of skin before regular use. Some oils
            are not safe around pets or during pregnancy. See our{" "}
            <Link to="/safety" className="text-[var(--terracotta)] underline">safety guide</Link> for more.
          </div>
        </div>
      </div>

      {/* Suggested products */}
      {suggested.length > 0 && (
        <section className="mt-24 pt-12 border-t border-[var(--border)]" data-testid="suggested-products">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <p className="eyebrow mb-2">You might also love</p>
              <h2 className="font-serif text-3xl text-[var(--ink-forest)] tracking-tight">
                More for your shelf
              </h2>
            </div>
            <Link to="/shop" className="text-sm font-medium text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-4">
              Shop all →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {suggested.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const CAT = {
  "diffuser-blends": "Diffuser Blends",
  "roller-bottles": "Roller Bottles",
  sprays: "Sprays & Mists",
  candles: "Candles",
  soaps: "Soaps & Bath",
  cleaning: "Natural Cleaning",
};
function categoryLabel(c) {
  return CAT[c] || c;
}

function PinterestIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.137.893 2.739a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
    </svg>
  );
}
